import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import type { SendOutcome } from '../lib/api'
import { getReadyForPickupOrders, lookupOrders } from '../lib/api'
import type { LookupableOrder } from '../types'
import { orderCode } from '../lib/order-code'
import { useSilentRefresh } from '../lib/useSilentRefresh'
import { CheckIcon, RefreshIcon } from '../components/Icons'

// ux3 (d): after a settled record the screen goes back to the pickup list after 2 s.
export const RETURN_TO_LIST_MS = 2_000

// ux3 (h) + artboard: one 64 px list row, logical alignment (text-start), navy tint on hover.
const ROW_CLASS =
  'flex w-full min-h-16 items-center justify-between gap-3 px-4 py-2 text-start hover:bg-navy-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy'

interface CollectScreenProps {
  isOnline: boolean
  onSubmit: (orderId: string, amountUsd: number) => Promise<SendOutcome>
}

interface SubmitResult {
  outcome: 'sent' | 'queued'
  collected: number
  expected: number
  mismatch: boolean
}

function parseAmount(input: string): number | null {
  if (input.trim() === '') return null
  const n = Number(input)
  if (!Number.isFinite(n)) return null
  return n
}

function toCents(n: number): number {
  return Math.round(n * 100)
}

function formatUsd(n: number): string {
  return `$${n.toFixed(2)}`
}

// bench-CASHIER-LOOKUP — half A of the till bench, on top of bench-CASHIER-TILL's custody
// instrument. Two defects fixed: (1) a cashier no longer types a cuid — they tap a pickup
// off the ready-for-pickup list, or search a phone/short-code and tap the match; never a
// free-typed order id, never an auto-selected guess when a search is ambiguous (§3g — every
// match, tap-list or lookup, is always rendered as a list requiring an explicit tap). (2)
// `expected` comes off the selected order (§3f) and is display-only — only `collected` is
// ever typed, so a mismatch means something.
export function CollectScreen({ isOnline, onSubmit }: CollectScreenProps) {
  const [tapList, setTapList] = useState<LookupableOrder[] | null>(null)
  const [tapListError, setTapListError] = useState<string | null>(null)
  const [loadingTapList, setLoadingTapList] = useState(true)

  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searchResults, setSearchResults] = useState<LookupableOrder[] | null>(null)

  const [selected, setSelected] = useState<LookupableOrder | null>(null)
  const [collectedInput, setCollectedInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SubmitResult | null>(null)

  const mounted = useRef(true)
  const tapListInFlight = useRef(false)
  const returnTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearReturnTimer = useCallback(() => {
    if (returnTimer.current !== null) {
      clearTimeout(returnTimer.current)
      returnTimer.current = null
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      clearReturnTimer()
    }
  }, [clearReturnTimer])

  // ux3 (c): one loader for the first load, the Refresh button and the silent 20 s refresh.
  // A silent load shows no "Loading" line and, if it fails, keeps the list already on screen.
  // A load never starts while another is running.
  const loadTapList = useCallback((silent: boolean = false) => {
    if (tapListInFlight.current) return
    tapListInFlight.current = true
    if (!silent) {
      setLoadingTapList(true)
      setTapListError(null)
    }
    getReadyForPickupOrders()
      .then((orders) => {
        if (!mounted.current) return
        setTapList(orders)
        setTapListError(null)
      })
      .catch(() => {
        if (mounted.current && !silent) setTapListError('Could not load the pickup list \u2014 try again.')
      })
      .finally(() => {
        tapListInFlight.current = false
        if (mounted.current) setLoadingTapList(false)
      })
  }, [])

  useEffect(() => {
    loadTapList()
  }, [loadTapList])

  useSilentRefresh(() => loadTapList(true))

  const collected = parseAmount(collectedInput)
  const expected = selected?.totalAmount ?? null
  const liveMismatch =
    expected !== null && collected !== null && toCents(expected) !== toCents(collected)

  const canSubmit = selected !== null && collected !== null && collected > 0 && !submitting
  const bannerPlace = selected === null ? 'order-first mb-6' : 'mt-6'

  function selectOrder(order: LookupableOrder) {
    clearReturnTimer()
    setSelected(order)
    setSearchResults(null)
    setQuery('')
    setCollectedInput('')
    setError(null)
    setResult(null)
  }

  function changeOrder() {
    clearReturnTimer()
    setSelected(null)
    setCollectedInput('')
    setError(null)
    setResult(null)
  }

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    setSearching(true)
    setSearchError(null)
    setSearchResults(null)
    try {
      const matches = await lookupOrders(q)
      setSearchResults(matches)
    } catch {
      setSearchError('Search failed — try again.')
    } finally {
      setSearching(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit || collected === null || selected === null) return

    setError(null)
    setResult(null)
    setSubmitting(true)
    try {
      const outcome = await onSubmit(selected.id, collected)
      if (outcome.outcome === 'rejected') {
        // bench-CASHIER-QUEUE-TRUTH.1 — a response ARRIVED and the server refused it. That is
        // a terminal fact on THIS press, not a queue state: "queued" is unreachable here. Name
        // the server's reason directly (409 → its "already recorded" message).
        setError(outcome.message)
        return
      }
      const mismatch = toCents(selected.totalAmount) !== toCents(collected)
      setResult({
        outcome: outcome.outcome,
        collected,
        expected: selected.totalAmount,
        mismatch,
      })
      setCollectedInput('')
      // ux3 (d): only a settled record (sent, amount matched) goes back to the list on its
      // own, keeping the green banner. A queued or mismatched record stays for the cashier.
      if (outcome.outcome === 'sent' && !mismatch) {
        clearReturnTimer()
        returnTimer.current = setTimeout(() => {
          returnTimer.current = null
          if (!mounted.current) return
          setSelected(null)
          setCollectedInput('')
          setError(null)
          loadTapList(true)
        }, RETURN_TO_LIST_MS)
      }
    } catch {
      setError('Could not submit — try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-fleet-50 px-6 py-8 font-body text-fleet-800">
      <div className="w-full max-w-sm mx-auto">
        <div>
          {/* The header and the Collect tab already name the screen (artboard); the title
              stays for screen readers. */}
          <h1 className="sr-only">Cash Collect</h1>
        </div>

        {!isOnline && (
          <div
            id="offline-banner"
            role="status"
            className="mb-4 rounded-lg bg-fleet-200 text-fleet-700 text-sm font-semibold px-4 py-3"
          >
            Offline — submissions will be queued and synced when the connection returns.
          </div>
        )}

        {/* ux3 (d): the result banner sits under the form while an order is open, and at
            the top of the list once the screen has gone back to it. */}
        <div className="flex flex-col">
        {selected === null ? (
          <div className="space-y-6">
            <form id="lookup-form" onSubmit={handleSearch} className="space-y-2">
              <label htmlFor="lookup-input" className="sr-only">
                Find by phone or code
              </label>
              <div className="flex gap-3">
                <input
                  id="lookup-input"
                  type="text"
                  value={query}
                  placeholder="Phone or order code"
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 min-w-0 min-h-12 rounded-xl border border-fleet-200 bg-white px-4 text-base placeholder:text-fleet-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                />
                <button
                  id="lookup-submit-btn"
                  type="submit"
                  disabled={query.trim() === '' || searching}
                  className="min-h-12 rounded-xl bg-navy hover:bg-navy/90 disabled:bg-fleet-300 text-white text-base font-semibold px-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
                >
                  {searching ? '…' : 'Find'}
                </button>
              </div>
              {searchError && (
                <div id="search-error" role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
                  {searchError}
                </div>
              )}
            </form>

            {searchResults !== null && (
              <div id="search-results">
                {searchResults.length === 0 ? (
                  <p id="search-empty" className="text-sm text-fleet-500">No matching order.</p>
                ) : (
                  <ul className="divide-y divide-fleet-200 overflow-hidden rounded-2xl border border-fleet-200 bg-white">
                    {searchResults.map((o) => (
                      <li key={o.id}>
                        <button
                          type="button"
                          data-order-id={o.id}
                          onClick={() => selectOrder(o)}
                          className={ROW_CLASS}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-fleet-900">
                              {orderCode(o)} &middot; {o.customerName ?? 'Unknown'}
                            </span>
                            <span className="block text-sm text-fleet-500">{o.phoneNormalized ?? <>&mdash;</>}</span>
                          </span>
                          <span className="text-lg font-bold text-navy">{formatUsd(o.totalAmount)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-fleet-900">Ready for pickup</h2>
                <button
                  id="collect-refresh-btn"
                  type="button"
                  onClick={() => loadTapList()}
                  disabled={loadingTapList}
                  className="min-h-tap inline-flex items-center gap-1.5 rounded-lg px-2 text-base font-semibold text-navy hover:bg-navy-tint disabled:text-fleet-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                >
                  <RefreshIcon className={loadingTapList ? 'h-4 w-4 shrink-0 animate-spin' : 'h-4 w-4 shrink-0'} />
                  <span>{loadingTapList ? 'Refreshing' : 'Refresh'}</span>
                </button>
              </div>
              {loadingTapList && <p className="text-sm text-fleet-500">Loading…</p>}
              {tapListError && (
                <div id="tap-list-error" role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
                  {tapListError}
                </div>
              )}
              {tapList !== null && tapList.length === 0 && !loadingTapList && (
                <p id="tap-list-empty" className="text-sm text-fleet-500">No pickups ready right now.</p>
              )}
              {tapList !== null && tapList.length > 0 && (
                <ul id="tap-list" className="divide-y divide-fleet-200 overflow-hidden rounded-2xl border border-fleet-200 bg-white">
                  {tapList.map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        data-order-id={o.id}
                        onClick={() => selectOrder(o)}
                        className={ROW_CLASS}
                      >
                        <span className="min-w-0 flex-1 font-semibold text-fleet-900">
                          {orderCode(o)} &middot; {o.customerName ?? 'Unknown'}
                        </span>
                        <span className="text-lg font-bold text-navy">{formatUsd(o.totalAmount)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div id="collect-card" className="rounded-2xl border-2 border-navy bg-white p-4 space-y-4">
            <div id="selected-order" className="text-fleet-900">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">{orderCode(selected)}</span>
                <button
                  id="change-order-btn"
                  type="button"
                  onClick={changeOrder}
                  className="min-h-tap rounded-lg px-2 text-sm font-semibold text-navy hover:bg-navy-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                >
                  Change
                </button>
              </div>
              <p className="text-sm text-fleet-600">
                {selected.customerName ?? 'Unknown'} · {selected.phoneNormalized ?? '—'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <span className="block text-sm font-semibold text-fleet-500 mb-1.5">Expected amount (USD)</span>
                <p
                  id="expected-display"
                  className="w-full rounded-xl bg-fleet-100 px-4 py-3 text-end text-3xl font-bold text-fleet-900"
                >
                  {formatUsd(selected.totalAmount)}
                </p>
              </div>

              <div>
                <label htmlFor="collected-input" className="block text-sm font-semibold text-fleet-500 mb-1.5">
                  Collected amount (USD)
                </label>
                <input
                  id="collected-input"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={collectedInput}
                  onChange={(e) => setCollectedInput(e.target.value)}
                  className="w-full min-h-14 rounded-xl border border-fleet-300 bg-white px-4 text-2xl font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                />
              </div>

              {liveMismatch && expected !== null && collected !== null && (
                <div
                  id="mismatch-banner"
                  role="alert"
                  className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3"
                >
                  Discrepancy: collected {formatUsd(collected)} does not match expected {formatUsd(expected)}
                  {' '}({collected > expected ? 'over' : 'short'} by {formatUsd(Math.abs(collected - expected))}).
                </div>
              )}

              {error && (
                <div id="error-banner" role="alert" className="rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3">
                  {error}
                </div>
              )}

              <button
                id="submit-btn"
                type="submit"
                disabled={!canSubmit}
                className="w-full min-h-[60px] inline-flex items-center justify-center gap-2 rounded-xl bg-navy hover:bg-navy/90 disabled:bg-fleet-300 text-white text-lg font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
              >
                <CheckIcon className="h-5 w-5 shrink-0" />
                {submitting ? 'Recording…' : 'Record collection'}
              </button>
            </form>
          </div>
        )}

            {result && (
              <div
                id="result-banner"
                role="status"
                className={
                  result.outcome === 'queued'
                    ? `${bannerPlace} rounded-lg bg-fleet-200 text-fleet-700 text-sm font-semibold px-4 py-3`
                    : result.mismatch
                      ? `${bannerPlace} rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3`
                      : `${bannerPlace} rounded-lg bg-jade-100 text-jade-700 text-sm font-semibold px-4 py-3`
                }
              >
                {result.outcome === 'queued' ? (
                  <>Queued — not yet settled. {formatUsd(result.collected)} will sync when back online.</>
                ) : result.mismatch ? (
                  <>
                    Recorded {formatUsd(result.collected)} — discrepancy vs expected {formatUsd(result.expected)}.
                  </>
                ) : (
                  <>Recorded {formatUsd(result.collected)} — settled.</>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
